import { Route } from '@angular/router';
import { ONBOARDING_SETUP_PATH, TOUR_PATH } from '@cdw-ae/common-data';
import { authGuard } from './common/guards/auth.guard';
import { inTestingFeatureGuard } from './common/guards/in-testing-feature.guard';
import { HeaderRouteResolver } from './common/services/routeResolver/route-resolver.service';
import { canLeaveOnboardingWizard } from './layouts/external/components/onboarding-setup/onboarding-leave.guard';
import { SplashComponent } from './layouts/external/components/splash/splash.component';
import { AccessManagementComponent } from './layouts/internal/components/administration/access-management/access-management.component';
import { DesignationEditComponent } from './layouts/internal/components/administration/access-management/designations/designation-edit/designation-edit.component';
import { OuCollectionEditComponent } from './layouts/internal/components/administration/access-management/ou-collections/ou-collection-edit/ou-collection-edit.component';
import { RoleEditComponent } from './layouts/internal/components/administration/access-management/roles/role-edit/role-edit.component';
import { UserCollectionEditComponent } from './layouts/internal/components/administration/access-management/user-collections/user-collection-edit/user-collection-edit.component';
import { AuditLogComponent } from './layouts/internal/components/administration/audit-log/audit-log.component';
import { AuditLogJobDetailComponent } from './layouts/internal/components/administration/audit-log-job-detail/audit-log-job-detail.component';
import { GlobalSettingsComponent } from './layouts/internal/components/administration/global-settings/global-settings.component';
import { DashboardComponent } from './layouts/internal/components/dashboard/dashboard.component';
// DeviceSearchComponent and ReportsComponent are now provided by InternalComponentsModule
import { DeviceSearchComponent } from './layouts/internal/components/device-search/device-search.component';
import { DeviceSummaryComponent } from './layouts/internal/components/device-summary/device-summary.component';
import { JobsDeviceProfileWipeComponentComponent } from './layouts/internal/components/jobs-device-profile-wipe/jobs-device-profile-wipe.component';
import { ReportViewerComponent } from './layouts/internal/components/reports/report-viewer/report-viewer.component';
import { ReportsComponent } from './layouts/internal/components/reports/reports.component';
import { InternalLayoutComponent } from './layouts/internal/internal-layout.component';

export const appRoutes: Route[] = [
  {
    path: 'welcome', // Explicit path for the welcome/login page
    component: SplashComponent,
    // Reset the tab title to the app name so a prior route's title (e.g. a
    // report name) doesn't persist on the login page after logout. (EDUS-2332)
    resolve: {
      preload: HeaderRouteResolver,
    },
    // Optional: Add a guard here to redirect logged-in users away from welcome
    // canActivate: [loggedInRedirectGuard]
  },
  {
    path: 'open/:type/:id',
    loadComponent: () =>
      import('./components/open-redirect/open-redirect.component').then(
        (m) => m.OpenRedirectComponent
      ),
    // Transient redirect route — reset the tab title to the app name so a prior
    // route's title doesn't stick. (EDUS-2332)
    resolve: {
      preload: HeaderRouteResolver,
    },
  },
  {
    // Every non-APP login-router verdict (EDUS-2726): NOT_LICENSED,
    // PROSPECT, NEEDS_SUPER_ADMIN, INTEGRATION_BROKEN, UNAVAILABLE and
    // CONFIG_IN_PROGRESS — all one modal, see docs/frontend/login-block-screens.md.
    // Outside authGuard — a blocked user has no app claims.
    path: 'login-status',
    loadComponent: () =>
      import(
        './layouts/external/components/login-status/login-status.component'
      ).then((m) => m.LoginStatusComponent),
    resolve: {
      preload: HeaderRouteResolver,
    },
  },
  {
    // Literal wizard route per AC-1.1 (EDUS-2680), hosting the wizard shell
    // (EDUS-2681). Outside authGuard — an onboarding SA has only the minimal wizard token.
    // Route paths take no leading slash, hence .slice(1) on the shared path.
    path: ONBOARDING_SETUP_PATH.slice(1),
    loadComponent: () =>
      import(
        './layouts/external/components/onboarding-setup/onboarding-setup.component'
      ).then((m) => m.OnboardingSetupComponent),
    resolve: {
      preload: HeaderRouteResolver,
    },
    // Abandonment protection (EDUS-2690 / AC-11.1): warn before leaving an
    // incomplete wizard. The component type is an `import type` so this stays
    // erased at runtime and the route keeps its lazy chunk.
    canDeactivate: [canLeaveOnboardingWizard],
  },
  {
    path: '', // Root path defaults to internal layout IF authenticated
    component: InternalLayoutComponent,
    canActivate: [authGuard], // Protect this route and its children
    children: [
      {
        path: 'dashboard',
        component: DashboardComponent,
        data: { headerTitle: 'Dashboard' },
        resolve: {
          preload: HeaderRouteResolver,
        },
      },
      {
        path: 'administration/access-management',
        component: AccessManagementComponent,
        data: { headerTitle: 'Administration' },
        canActivate: [inTestingFeatureGuard],
        resolve: {
          preload: HeaderRouteResolver,
        },
      },
      // {
      //   path: 'administration/team-permissions',
      //   component: TeamPermissionsComponent,
      //   data: { headerTitle: 'Administration' },
      //   resolve: {
      //     preload: HeaderRouteResolver,
      //   },
      // },
      // Commented out because this component might not even make it to production and removed entirely
      {
        path: 'administration/audit-log',
        component: AuditLogComponent,
        data: { headerTitle: 'Administration' },
        resolve: {
          preload: HeaderRouteResolver,
        },
      },
      {
        path: 'administration/audit-log/job/:jobId',
        component: AuditLogJobDetailComponent,
        data: { headerTitle: 'Audit Log Detail' },
        resolve: {
          preload: HeaderRouteResolver,
        },
      },
      {
        path: 'administration/global-settings',
        component: GlobalSettingsComponent,
        data: { headerTitle: 'Administration' },
        resolve: {
          preload: HeaderRouteResolver,
        },
      },
      {
        path: 'administration/access-management/role/edit/new',
        component: RoleEditComponent,
        data: { headerTitle: 'Add New Role' },
        canActivate: [inTestingFeatureGuard],
        resolve: {
          preload: HeaderRouteResolver,
        },
        canDeactivate: [
          (component: RoleEditComponent) =>
            component.confirmNavigationWithUnsavedChanges(),
        ],
      },
      {
        path: 'administration/access-management/role/edit/:id',
        component: RoleEditComponent,
        data: { headerTitle: 'Edit Role' },
        canActivate: [inTestingFeatureGuard],
        resolve: {
          preload: HeaderRouteResolver,
        },
        canDeactivate: [
          (component: RoleEditComponent) =>
            component.confirmNavigationWithUnsavedChanges(),
        ],
      },
      {
        path: 'administration/access-management/user-collection/edit/new',
        component: UserCollectionEditComponent,
        data: { headerTitle: 'Add New User Collection' },
        canActivate: [inTestingFeatureGuard],
        resolve: {
          preload: HeaderRouteResolver,
        },
        canDeactivate: [
          (component: UserCollectionEditComponent) =>
            component.confirmNavigationWithUnsavedChanges(),
        ],
      },
      {
        path: 'administration/access-management/user-collection/edit/:id',
        component: UserCollectionEditComponent,
        data: { headerTitle: 'Edit User Collection' },
        canActivate: [inTestingFeatureGuard],
        resolve: {
          preload: HeaderRouteResolver,
        },
        canDeactivate: [
          (component: UserCollectionEditComponent) =>
            component.confirmNavigationWithUnsavedChanges(),
        ],
      },
      {
        path: 'administration/access-management/org-unit-collection/edit/new',
        component: OuCollectionEditComponent,
        data: { headerTitle: 'Add New OU Collection' },
        canActivate: [inTestingFeatureGuard],
        resolve: {
          preload: HeaderRouteResolver,
        },
        canDeactivate: [
          (component: OuCollectionEditComponent) =>
            component.confirmNavigationWithUnsavedChanges(),
        ],
      },
      {
        path: 'administration/access-management/org-unit-collection/edit/:id',
        component: OuCollectionEditComponent,
        data: { headerTitle: 'Edit OU Collection' },
        canActivate: [inTestingFeatureGuard],
        resolve: {
          preload: HeaderRouteResolver,
        },
        canDeactivate: [
          (component: OuCollectionEditComponent) =>
            component.confirmNavigationWithUnsavedChanges(),
        ],
      },
      {
        path: 'administration/access-management/designation/edit/:id',
        component: DesignationEditComponent,
        data: { headerTitle: 'Add New Designation' },
        canActivate: [inTestingFeatureGuard],
        resolve: {
          preload: HeaderRouteResolver,
        },
        canDeactivate: [
          (component: DesignationEditComponent) =>
            component.confirmNavigationWithUnsavedChanges(),
        ],
      },
      {
        path: 'administration/access-management/designation/edit/:id',
        component: DesignationEditComponent,
        data: { headerTitle: 'Edit Designation' },
        canActivate: [inTestingFeatureGuard],
        resolve: {
          preload: HeaderRouteResolver,
        },
        canDeactivate: [
          (component: DesignationEditComponent) =>
            component.confirmNavigationWithUnsavedChanges(),
        ],
      },
      {
        path: 'deviceSearch',
        component: DeviceSearchComponent,
        data: { headerTitle: 'Device Search' },
        resolve: {
          preload: HeaderRouteResolver,
        },
        canDeactivate: [
          (component: DeviceSearchComponent) =>
            component.confirmNavigationWithUnsavedChanges(),
        ],
      },
      {
        path: 'device-summary/:id',
        component: DeviceSummaryComponent,
        data: { headerTitle: 'Device Summary' },
        resolve: {
          preload: HeaderRouteResolver,
        },
      },
      {
        path: 'reports',
        component: ReportsComponent,
        data: { headerTitle: 'Reports' },
        resolve: {
          preload: HeaderRouteResolver,
        },
      },
      {
        path: 'report-viewer/:reportType',
        component: ReportViewerComponent,
        data: { headerTitle: 'Report Viewer' },
        resolve: {
          preload: HeaderRouteResolver,
        },
      },
      {
        path: 'jobs/devices/chrome-profile-wipe',
        data: { headerTitle: 'Chrome Profile Wipe' },
        component: JobsDeviceProfileWipeComponentComponent,
        resolve: {
          preload: HeaderRouteResolver,
        },
      },
      {
        path: 'account-profile',
        resolve: {
          preload: HeaderRouteResolver,
        },
        data: { headerTitle: 'Account Profile' },
        loadComponent: () =>
          import(
            './layouts/internal/components/account-profile/account-profile.component'
          ).then((m) => m.AccountProfileComponent),
      },
      {
        // Product-tour landing page (EDUS-2852). The Userlane guide is
        // targeted by PAGE SEGMENT on this URL, so changing the path silently
        // breaks the guide's targeting. See tour.component.ts.
        //
        // The header title is the page's welcome (per the Figma), NOT the
        // sidebar's "Take a Tour" label — a first-time user lands here without
        // having clicked anything, so "Take a Tour" would have read as a
        // command rather than a greeting.
        path: TOUR_PATH.slice(1),
        data: { headerTitle: 'Welcome to Gopher for Chrome Premium' },
        loadComponent: () =>
          import('./layouts/internal/components/tour/tour.component').then(
            (m) => m.TourComponent
          ),
        resolve: {
          preload: HeaderRouteResolver,
        },
      },
      { path: '', redirectTo: 'dashboard', pathMatch: 'full' }, // Default child route
    ],
  },
  // Optional: Redirect any other unmatched routes
  // If not logged in, authGuard on '' will redirect to '/welcome' anyway
  // If logged in, this could redirect to '/overview' or a 404 page
  { path: '**', redirectTo: '', pathMatch: 'full' },
];
